/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment } from 'react';
import { RouteComponentProps, navigate, useLocation } from '@reach/router';
import { Box, Divider, FormHelperText, Typography } from '@material-ui/core';

import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import ROUTES from '../../../../../routes';
import colors from '../../../../../themes/main-colors';
import DatePicker from '../../../../date-picker/DatePicker';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import useFormState from '../../../../../hooks/useFormState';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import { chargerInstallInfoValidateFunction } from '../rebateOnlyForm.rules';
import {
  ApplicationErSectionType,
  ErChargerInstallInfo,
  RebateSmartCharging,
  SectionErRebateConfirm,
  SectionErRebateInfo,
} from '../../../../../__generated__/pgeplus-types';
import {
  getPreRebateEligibilityEvRebate,
  getSmartChargingRebate,
  getPreRebateEligibilityConfirmationState,
  getRebateTypeCategory,
  isIQBDQuestionApplicable,
  isVehiclePathCustomer,
} from '../rebateOnlyForm.utils';
import useUtil from '../../../../../hooks/pge-plus/useUtil';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import { rebateConfirmERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import ContactInfoRow from '../../../../pge-plus-form/ContactInfo/ContactInfoRow';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import Grid from '@material-ui/core/Grid';
import RadioButtonGroup from '../../../../pge-plus-common/RadioButtonGroup';

type ChargerInformationState = {
  backButtonRoute: string;
};

const ChargerInformation: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const location = useLocation();
  const { backButtonRoute } = (location.state || {}) as ChargerInformationState;

  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_REBATE_CONFIRM,
  );
  const { content: contentMCG } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_EV_REBATES_FLOW_MCG,
  );

  const {
    formState: { rebateConfirm, rebateInfo, application },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const { updateSectionRebateConfirm } = useUpdateERSections();
  const { setErrorNotification } = useUtil();
  const { wrapWithLoader } = useWrapWithLoader();

  const sectionDataRebateConfirm: SectionErRebateConfirm = getSectionData(
    ApplicationErSectionType.ErRebateConfirm,
  );

  const sectionDataRebateInfo: SectionErRebateInfo = getSectionData(
    ApplicationErSectionType.ErRebateInfo,
  );

  // CUP-24444, CUP-24445: Drives both the vehicle-path submit routing below and the install-date field visibility
  const isVehiclePath = isVehiclePathCustomer(rebateInfo);

  const hasChargerInfo = Boolean(
    rebateInfo.chargerValidation?.product?.modelNo ||
      rebateInfo.qualifiedProductInfo?.serialNumber ||
      rebateInfo.smartCharging?.modelNumber,
  );

  const form = useFormState(rebateConfirm.chargerInstallInfo || {}, {
    validate: chargerInstallInfoValidateFunction(),
    validationContext: {
      content,
      params: { isVehiclePath, hasChargerInfo },
    },
  });

  const isByocCustomer =
    rebateConfirm.chargerInstallInfo?.isChargerPurchased === false;

  const isIQBDApplicable = isIQBDQuestionApplicable(
    sectionDataRebateInfo.data.rebateInfo.existingEnrollments,
  );

  const byocBackRoute = isIQBDApplicable
    ? ROUTES.PGE_PLUS_REBATE_ONLY_HIGHER_REBATES_CHECK
    : ROUTES.PGE_PLUS_REBATE_ONLY_CUSTOMER_INFORMATION;

  const backRouteState = () => {
    const rebateSmartCharging = getSmartChargingRebate(
      rebateConfirm.preRebateEligibility2,
      rebateConfirm.preRebateEligibility1,
    );

    const backState: any = {
      chargerAmount: rebateSmartCharging?.charger ?? rebateSmartCharging?.amount,
      panelUpgradeAmount: rebateSmartCharging?.panelUpgrade,
      isAbleToRequestIQBD: isIQBDApplicable,
    };

    //Navigation logic
    // CUP-24444: Extracted the inline iqbd/higher/standard if-else chain into the shared getRebateTypeCategory() helper..
    backState['rebateType'] = getRebateTypeCategory(
      sectionDataRebateInfo.data.rebateInfo.existingEnrollments.iqbd,
      rebateSmartCharging?.isHigherDiscount,
      rebateConfirm.iqbd?.checkEligibility,
    );
    return backState;
  };

  const handleSubmit = wrapWithLoader(async (data: ErChargerInstallInfo) => {
    try {
      const response = await updateSectionRebateConfirm(
        rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerInstallInfo: data,
            rebateQualification: {
              ...rebateConfirm.rebateQualification,
              willConnectVehicleManufacturerApp: undefined,
            },
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
      );

      if (!response.data?.updateSectionERRebateConfirm.section.errors.length) {
        if (response.data?.updateSectionERRebateConfirm?.section.data) {
          const updatedValues = rebateConfirmERFormStateAdapter().toFormState(
            response.data?.updateSectionERRebateConfirm?.section.data,
          );
          updateFormState('rebateConfirm', {
            ...updatedValues,
          });
        }

        const evRebate =
          response.data?.updateSectionERRebateConfirm.section.data
            .preRebateEligibility2?.evRebate;

        if (!isVehiclePath) {
          // Note: Charger-path: unchanged, fresh response only, throws if missing/unrecognized
          if (evRebate?.__typename === 'RebateBYOC') {
            void navigate(ROUTES.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION, {
              state: {
                ...getPreRebateEligibilityConfirmationState(evRebate),
                nextButtonRoute: ROUTES.PGE_PLUS_REBATE_ONLY_CONNECT_CHARGER,
              },
            });
          } else if (
            evRebate?.__typename === 'RebateSmartCharging' ||
            evRebate?.__typename === 'RebateEVPulse'
          ) {
            void navigate(ROUTES.PGE_PLUS_REBATE_ONLY_CHARGER_INSTALLATION_COSTS);
          } else {
            throw new Error();
          }
        } else {
          // CUP-24444: Vehicle-path routes based on the user's charger-purchased answer, not the rebate type
          if (data.isChargerPurchased === false) {
            const sectionData =
              response.data?.updateSectionERRebateConfirm.section.data;
            void navigate(ROUTES.PGE_PLUS_REBATE_ONLY_ELIGIBILITY_CONFIRMATION, {
              state: {
                ...getPreRebateEligibilityConfirmationState(
                  getPreRebateEligibilityEvRebate(
                    sectionData?.preRebateEligibility2,
                    sectionData?.preRebateEligibility1,
                  ),
                  false,
                  true,
                  sectionData?.rebateInfo?.existingEnrollments?.iqbd,
                  sectionData?.rebateConfirmInput?.iqbdQualification
                    ?.checkEligibility,
                ),
                nextButtonRoute: ROUTES.PGE_PLUS_REBATE_ONLY_CONNECT_VEHICLE,
              },
            });
          } else {
            void navigate(ROUTES.PGE_PLUS_REBATE_ONLY_CHARGER_INSTALLATION_COSTS);
          }
        }
      } else {
        throw new Error();
      }
    } catch (error) {
      console.log('error', error);
      setErrorNotification(true);
    }
  });

  const contactInfoBox = {
    border: `1px solid ${colors.sparkBlue}`,
    borderRadius: 5,
    margin: '20px 0 20px 0',
    padding: 20,
    fontSize: 16,
    boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
  };

  const divider = {
    color: colors.lightGray1,
  };

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateConfirm({
        ...rebateConfirmERFormStateAdapter().fromFormState(
          {
            ...rebateConfirm,
            chargerInstallInfo: form.values,
          },
          application?.id!,
          sectionDataRebateConfirm?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERRebateConfirm?.validation?.errors ||
        response.data?.updateSectionERRebateConfirm?.section?.errors.length !==
          0
      ) {
        throw new Error();
      } else {
        void navigate(ROUTES.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <form onSubmit={form.submit(values => handleSubmit(values), console.log)}>
      <Box>
        {hasChargerInfo && (
          <Box display={'flex'} justifyContent={'center'}>
            <Grid css={{ ...contactInfoBox }} xs={12} md={8}>
              <Box fontWeight="700" display="flex" justifyContent="space-between">
                <Typography
                  variant={'body1'}
                  css={{ fontWeight: 700, fontSize: 20, marginBottom: 15 }}
                >
                  {content.get('YOUR_CHARGER_INFO')}
                </Typography>
              </Box>

              <Divider css={{ ...divider, margin: '16px 0' }} />

              <ContactInfoRow
                label={content.get('MODEL_NUMBER')}
                value={rebateInfo.chargerValidation?.product?.modelNo || rebateInfo.smartCharging?.modelNumber || content.get('REUSABLE_NOT_APPLICABLE') || 'N/A'}
              />
              <ContactInfoRow
                label={content.get('SERIAL_NUMBER')}
                value={rebateInfo.qualifiedProductInfo?.serialNumber || content.get('REUSABLE_NOT_APPLICABLE') || 'N/A'}
              />
            </Grid>
          </Box>
        )}
        <Box>
          <Typography
            css={{
              fontFamily: 'Untitled-Sans',
              fontSize: 20,
              fontWeight: 'bold',
              lineHeight: '26px',
            }}
          >
            {content.get('IS_CHARGER_EXISTING_OR_PURCHASED')}
          </Typography>
          <Box display={'flex'} justifyContent={'center'} mt={4}>
            <Grid xs={12} md={6}>
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('PURCHASED') || '',
                    value: true,
                  },
                  {
                    label: content.get('ALREADY_PRESENT') || '',
                    value: false,
                  },
                ]}
                state={form.values.isChargerPurchased as boolean | undefined}
                setState={val => {
                  void form.setValue('isChargerPurchased', val);
                }}
              />
            </Grid>
          </Box>
          {form.errors.isChargerPurchased && (
            <FormHelperText error>
              {form.errors.isChargerPurchased}
            </FormHelperText>
          )}
        </Box>
        {form.values.isChargerPurchased === true && (
          <Fragment>

            {/* CUP-24445: Installation-date question hidden for vehicle-path customers.. business confirmed it's not needed for the vehicle flows */}
            {!isVehiclePath && (
              <Box
                css={{
                  width: '100%',
                  marginBottom: '24px',
                  marginTop: '24px',
                  '&>div': {
                    width: '100%',
                  },
                }}
              >
                <Typography
                  css={{
                    fontFamily: 'Untitled-Sans',
                    fontSize: 20,
                    fontWeight: 'bold',
                    lineHeight: '26px',
                  }}
                >
                  {content.get('WHEN_WAS_CHARGER_INSTALLED')}
                </Typography>
                <DatePicker
                  name={content.get('SELECTED_DATE')}
                  label={content.get('SELECTED_DATE')}
                  value={form.values.installationDate}
                  onChange={({ target: { value } }: any) => {
                    if (value) {
                      void form.setValue('installationDate', value);
                    }
                  }}
                />
                {form.errors.installationDate && (
                  <FormHelperText error>
                    {form.errors.installationDate}
                  </FormHelperText>
                )}
              </Box>
            )}

            <Box css={{
                  width: '100%',
                  marginBottom: '24px',
                  marginTop: '24px',
                  '&>div': {
                    width: '100%',
                  },
            }}>
              <Typography
                css={{
                  fontFamily: 'Untitled-Sans',
                  fontSize: 20,
                  fontWeight: 'bold',
                  lineHeight: '26px',
                }}
              >
                {contentMCG.get('PGE_PLUS_EV_REBATES_FLOW_WAS_PANEL_REPLACED')}
              </Typography>
              <Typography css={{ marginTop: '24px' }}>
                {inlineRichText(
                  contentMCG.get(
                    'PGE_PLUS_EV_REBATES_FLOW_WAS_PANEL_REPLACED_DESC',
                  ),
                )}
              </Typography>
              <Box display={'flex'} justifyContent={'center'} mt={4}>
                <Grid xs={12} md={6}>
                  <RadioButtonGroup
                    options={[
                      {
                        label: content.get('REUSABLE_YES') || '',
                        value: content.get('REUSABLE_YES') || '',
                      },
                      {
                        label: content.get('REUSABLE_NO') || '',
                        value: content.get('REUSABLE_NO') || '',
                      },
                    ]}
                    state={
                      form.values.panelUpgraded === true
                        ? content.get('REUSABLE_YES') || ''
                        : form.values.panelUpgraded === false
                        ? content.get('REUSABLE_NO') || ''
                        : ''
                    }
                    setState={val => {
                      void form.setValue(
                        'panelUpgraded',
                        val === content.get('REUSABLE_YES'),
                      );
                    }}
                  />
                </Grid>
              </Box>
              {form.errors.panelUpgraded && (
                <FormHelperText error>
                  {form.errors.panelUpgraded}
                </FormHelperText>
              )}
            </Box>
          </Fragment>
        )}
        <Divider css={{ ...divider, margin: '24px 0' }} />
        <ProceedOrCancelButton
          back={
            isByocCustomer
              ? byocBackRoute
              : backButtonRoute ||
                (backRouteState()?.rebateType === 'standard'
                  ? ROUTES.PGE_PLUS_REBATE_ONLY_STANDARD_REBATES_ELIGIBILE
                  : ROUTES.PGE_PLUS_REBATE_ONLY_HIGHER_REBATES_ELIGIBILE)
          }
          backRouteState={backRouteState()}
          backLabel={content.get('REUSABLE_BUTTON_BACK')}
          // CUP-24444, CUP-24445: Next disabled until answered - install-date skipped for vehicle-path
          disable={
            form.values.isChargerPurchased === undefined ||
            form.values.isChargerPurchased === null ||
            (form.values.isChargerPurchased === true &&
              ((!isVehiclePath &&
                (form.values.installationDate === undefined ||
                  form.values.installationDate === null)) ||
                form.values.panelUpgraded === undefined ||
                form.values.panelUpgraded === null))
          }
        />
      </Box>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
    </form>
  );
};

export default ChargerInformation;
